/* eslint-disable no-param-reassign */
import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import rights from '../../../../components/rights';
import Filter from './filter';
import svg from '$$helpers/svgToURI';
import icon_resource from '../../../../svg/default/default-ava.svg';

class DataProcessor {
  constructor() {
    this.config = null;
    this.ganttInstance = null;
  }

  init(instance, config) {
    this.ganttInstance = instance;
    this.config = config;
  }

  prepareData() {
    const preparedData = [];
    let projectIds = this.config.projectIds;

    projectIds = Filter.filterProjects(projectIds);

    let resourcesData = this.ganttInstance.ganttDataApi.getResourcesByProjectIdsAndType(projectIds, constants.RESOURCE_TIME_TYPE);

    resourcesData = _.sortBy(resourcesData, [o => !o.userId, 'name']); // default sorting

    if (this.config.isJira) {
      resourcesData = resourcesData.filter(res => !res.email);
    }

    resourcesData = Filter.filterResources(resourcesData, projectIds);

    if (this.config.type !== constants.WORKLOAD_TYPES.panel && Filter.isFilteredByUnassigned()) {
      resourcesData.push({
        id: 'unassigned',
        name: __('notAssigned'),
        unassigned: true,
        photo: svg(icon_resource),
        customDays: [],
        workingDays: [1, 2, 3, 4, 5, 6, 7],
        workingHours: 0,
      });
    }

    this.config.calculationMode = Filter.isFilteredByProjects() ? 'scopedProjects' : this.config.calculationMode;

    resourcesData.forEach((resourceData, i) => {
      const isSeparator = !resourceData.userId && resourcesData[i - 1] && resourcesData[i - 1].userId;
      const preparedResourceData = this.prepareResourceData(resourceData, projectIds, isSeparator);

      preparedData.push(preparedResourceData);

      if (this.config.openedResources[preparedResourceData.id]) {
        const resourceTasksItem = {
          ...preparedResourceData,
          id: preparedResourceData.id + this.config.RESOURCE_TASK_ID_POSTFIX,
          isTasksItem: true,
        };

        if (this.config.type === constants.WORKLOAD_TYPES.panel) {
          // clear invisible tasks from taskslayout in current scope of projects
          resourceTasksItem.tasksByGanttId = resourceTasksItem.tasksByGanttId.filter(o => this.ganttInstance.isTaskExists(o.task_id));
        } else {
          const tasksToParse = [];

          resourceTasksItem.tasksByGanttId.forEach(o => {
            if (!this.ganttInstance.isTaskExists(o.task_id)) {
              tasksToParse.push(o.taskData);
            }
          });

          tasksToParse.length && this.ganttInstance.silent(() => {
            this.ganttInstance.parse({ data: tasksToParse });
          });
        }

        preparedData.push(resourceTasksItem);
      }
    });

    if (preparedData.length) {
      preparedData[preparedData.length - 1].isLast = true;
    }

    return preparedData;
  }

  prepareResourceData(resourceData, projectIds, isSeparator) {
    const item = {
      ..._.cloneDeep(resourceData),
      id: resourceData.id,
      resource_id: resourceData.id,
      name: resourceData.name,
      photo: resourceData.photo || resourceData.resourcePhoto,
      render_type: 'workload',
      selectedTaskId: null,
      accessToPersonalCalendar: rights.account.hasRight('team_and_resources_settings'),
      separator: isSeparator,
    };
    let resourceTasks = [];

    if (resourceData.unassigned) {
      resourceTasks = this.ganttInstance.ganttDataApi.getUnassignedTasksForWorkload(projectIds);
    } else if (this.config.calculationMode === 'allProjects') {
      resourceTasks = this.ganttInstance.ganttDataApi.getTasksForWorkload(item.resource_id);
    } else if (this.config.calculationMode === 'scopedProjects') {
      resourceTasks = this.ganttInstance.ganttDataApi.getScopedTasksForWorkload(item.resource_id, projectIds);
    }

    resourceTasks = resourceTasks.filter(t => {
      let startCond = false;
      let endCond = false;

      if (this.ganttInstance.config.start_date && this.ganttInstance.config.end_date) {
        startCond = t.taskData.start_date.valueOf() > this.ganttInstance.config.end_date.valueOf();
        endCond = t.taskData.end_date.valueOf() < this.ganttInstance.config.start_date.valueOf();
      }

      t.taskData.calendar_id = t.gantt_id;

      return t.taskData.type !== this.ganttInstance.config.types.project && !(startCond || endCond);
    });

    if (Filter.isActive()) {
      const filteredTasksMap = {};
      const filteredTasks = Filter.filterTasks(resourceTasks.map(task => task.taskData));

      filteredTasks.forEach(task => {
        filteredTasksMap[task.id] = task;
      });

      resourceTasks = resourceTasks.filter(task => filteredTasksMap[task.task_id]);
    }

    item.tasksByGanttId = resourceTasks;

    return item;
  }

  prepareWorkerConfig() {
    const totalEstimate = this.ganttInstance.getTaskByIndex(0) || { start_date: this.ganttInstance.config.start_date, end_date: this.ganttInstance.config.end_date };
    const timelineScale = this.ganttInstance.$ui.getView('workloadTimeline').getScale();
    const ganttConfig = this.ganttInstance.config;

    return {
      projectConfigs: this._prepareProjectConfigs(),
      scale: timelineScale.unit,
      ganttConfig: {
        work_time: ganttConfig.work_time,
        duration_unit: ganttConfig.duration_unit,
        start_on_monday: ganttConfig.start_on_monday,
      },
      rowHeight: this.config.ROW_HEIGHT,
      timelineData: {
        count: timelineScale.count,
        width: timelineScale.width,
        left: timelineScale.left,
        trace_x_ascending: timelineScale.trace_x_ascending,
        totalStart: this.ganttInstance.date[`${ganttConfig.durationData.mode}_start`](new Date(totalEstimate.start_date)),
        totalEnd: this.ganttInstance.date.add(this.ganttInstance.date[`${ganttConfig.durationData.mode}_start`](new Date(totalEstimate.end_date)), 1, ganttConfig.durationData.mode),
        rangeStart: moment(ganttConfig.start_date).toDate(),
        rangeEnd: moment(ganttConfig.end_date).toDate(),
      },
    };
  }

  _prepareProjectConfigs() {
    const projectConfigs = [];

    const ganttIds = this.ganttInstance.ganttDataApi.getAllProjectTasksData().map(o => o.id);

    ganttIds.forEach(ganttId => {
      // const calendar = this.ganttInstance.getCalendar(ganttId);
      let projectConfig = this.ganttInstance.ganttDataApi.getProjectConfigById(ganttId);
      const customDays = this.ganttInstance.ganttDataApi.getCustomDaysForProject(ganttId);

      // if (!calendar) {
      //   return;
      // }

      if (!this.ganttInstance.ganttDataApi.getProjectItem(ganttId)) {
        projectConfig = this.ganttInstance.ganttDataApi.getProjectTasksData(ganttId).workloadData;
      }

      const daysHours = [];

      const tasks = this.ganttInstance.ganttDataApi.getTasksForGantt(ganttId).data;
      const totalEstimate = _.clone(tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent) || {});
      const workingDays = [...projectConfig.durationData.showDay];

      workingDays.forEach((day, index) => {
        if (+day === 7) {
          workingDays[index] = 0;
        }
      });

      for (let i = 0; i < 7; i++) {
        if (workingDays.includes(i)) {
          daysHours.push(projectConfig.durationData.showTime);
        } else {
          daysHours.push(false);
        }
      }

      projectConfigs.push({
        calendar: {
          id: ganttId,
          worktime: {
            hours: projectConfig.durationData.showTime,
            days: daysHours,
          },
        },
        ganttId,
        ganttConfig: {
          estimation_mode: projectConfig.estimation_mode,
          apply_resource_calendar: projectConfig.apply_resource_calendar,
        },
        customDays: customDays || [],
        totalEstimate,
      });
    });

    return projectConfigs;
  }

  prepareDataForSmartScroll(data) {
    // calculates positions for rendering workload rows
    let top = 0;
    let result = [];
    const coords = this.config.resourcesLayout;

    result = data.map((item, i) => {
      if (!coords[item.id]) {
        throw new Error('workload layout coords calculation error');
      }
      if (item.id === 'unassigned-tasks') {
        const maxHeight = this.config.countOfUnassignedTaskRows * this.config.ROW_HEIGHT;

        coords[item.id].visibleHeight = coords[item.id].height;

        if (coords[item.id].height > maxHeight) {
          coords[item.id].visibleHeight = maxHeight + this.config.ROW_HEIGHT;
          item.isPartial = true;
        }
      }

      coords[item.id].top = top;
      top += coords[item.id].visibleHeight || coords[item.id].height;

      item.lastChild = false;
      item.firstChild = false;
      item.row_height = coords[item.id].visibleHeight || coords[item.id].height;

      if (data.length - 1 === i) {
        item.lastChild = true;
      }

      if (data.length - 2 === i) {
        item.prevLastChild = true;
      }

      if (i === 0) {
        item.firstChild = true;
      }

      return item;
    });

    coords.totalHeight = top;

    return result;
  }

  applyCacheToData(preparedData, cache) {
    const resultData = [];
    const selectedTask = this.config.selectedTask;
    let needReCalculate = false;

    preparedData.forEach(item => {
      if (item) {
        if (cache[item?.id]) {
          item = {
            ...cache[item.id],
            ...{ tasksByGanttId: item.tasksByGanttId },
          };
          item.selectedTaskId = null;
          item._fromCache = true;
        } else {
          needReCalculate = true;
        }

        if (this.ganttInstance.config.durationData.mode === 'day'
          && selectedTask.id
          && item.id === selectedTask.resourceId + this.config.RESOURCE_TASK_ID_POSTFIX) {
          const task = this.ganttInstance.isTaskExists(selectedTask.id) && this.ganttInstance.getTask(this.config.selectedTask.id);

          if (task) {
            item.selectedTaskId = task.id;
            needReCalculate = true;
          }
        }

        item.opened = this.config.openedResources[item.id];
        resultData.push(item);
      }
    });

    return {
      data: resultData,
      needReCalculate,
    };
  }
}

export default new DataProcessor();
